import { useState } from "react";
import { cn } from "@/lib/utils";
import { money } from "@/lib/api";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export const PageHeader = ({ eyebrow, title, children, testid = "page-title" }) => (
  <div className="fade-up mb-8 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
    <div className="min-w-0">
      {eyebrow && <p className="label-eyebrow mb-2">{eyebrow}</p>}
      <h1 className="font-display text-3xl font-bold tracking-tight sm:text-4xl" data-testid={testid}>{title}</h1>
    </div>
    <div className="no-print flex flex-wrap items-center gap-2">{children}</div>
  </div>
);

export const Kpi = ({ label, value, sub, tone = "default", icon: Icon, testid, delay = 0 }) => {
  const tones = { default: "text-foreground", green: "text-emerald-700", red: "text-red-600", blue: "text-blue-700", amber: "text-amber-600" };
  return (
    <div className="panel lift fade-up p-5" style={{ animationDelay: `${delay}ms` }} data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="label-eyebrow">{label}</span>
        {Icon && <Icon size={18} className="text-muted-foreground" />}
      </div>
      <div className={cn("mt-3 font-num text-xl font-semibold sm:text-2xl", tones[tone])}>{value}</div>
      {sub && <div className="mt-1 text-xs text-muted-foreground">{sub}</div>}
    </div>
  );
};

export const NativeSelect = ({ className, children, ...p }) => (
  <select className={cn("h-9 w-full rounded-[4px] border border-input bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30", className)} {...p}>
    {children}
  </select>
);

export const Field = ({ label, children, className }) => (
  <label className={cn("block space-y-1.5", className)}>
    <span className="label-eyebrow">{label}</span>
    {children}
  </label>
);

const BADGE = {
  sale: "bg-blue-50 text-blue-700 border-blue-200",
  purchase: "bg-amber-50 text-amber-700 border-amber-200",
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  invoiced: "bg-emerald-50 text-emerald-700 border-emerald-200",
  approved: "bg-blue-50 text-blue-700 border-blue-200",
  partial: "bg-orange-50 text-orange-700 border-orange-200",
  shipped: "bg-emerald-50 text-emerald-700 border-emerald-200",
  closed: "bg-zinc-100 text-zinc-600 border-zinc-200",
  cancelled: "bg-zinc-100 text-zinc-500 border-zinc-200",
  collection: "bg-emerald-50 text-emerald-700 border-emerald-200",
  payment: "bg-red-50 text-red-700 border-red-200",
  low: "bg-red-50 text-red-700 border-red-200",
  neutral: "bg-zinc-50 text-zinc-700 border-zinc-200",
};
export const Tag = ({ tone = "neutral", children, testid }) => (
  <span data-testid={testid} className={cn("inline-flex items-center whitespace-nowrap rounded-[3px] border px-1.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide", BADGE[tone])}>
    {children}
  </span>
);

export const Balance = ({ v, className }) => (
  <span className={cn("font-num", v > 0.004 ? "text-emerald-700" : v < -0.004 ? "text-red-600" : "text-muted-foreground", className)}>
    {money(Math.abs(v))} <span className="text-[10px] font-semibold">{v > 0.004 ? "(B)" : v < -0.004 ? "(A)" : ""}</span>
  </span>
);

export const Empty = ({ cols, text = "Kayıt bulunamadı" }) => (
  <tr><td colSpan={cols} className="py-10 text-center text-sm text-muted-foreground" data-testid="empty-state">{text}</td></tr>
);

export const Th = ({ children, right, className }) => (
  <th className={cn("whitespace-nowrap border-b border-border bg-muted/60 px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground", right ? "text-right" : "text-left", className)}>{children}</th>
);
export const Td = ({ children, right, className, ...p }) => (
  <td className={cn("border-b border-border/70 px-3 py-2.5 text-sm", right && "text-right font-num", className)} {...p}>{children}</td>
);

export function Confirm({ trigger, title, description, onConfirm, testid = "confirm-action-button" }) {
  const [busy, setBusy] = useState(false);
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>{trigger}</AlertDialogTrigger>
      <AlertDialogContent className="rounded-[4px]">
        <AlertDialogHeader>
          <AlertDialogTitle className="font-display">{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="confirm-cancel-button">Vazgeç</AlertDialogCancel>
          <AlertDialogAction data-testid={testid} disabled={busy}
            onClick={async () => { setBusy(true); await onConfirm(); setBusy(false); }}>Onayla</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

const ACCENT = { blue: "bg-blue-600", amber: "bg-amber-500", green: "bg-emerald-600", red: "bg-red-600", zinc: "bg-zinc-900", orange: "bg-orange-500" };

export const Panel = ({ title, icon: Icon, accent = "zinc", right, children, testid, className }) => (
  <section className={cn("fade-up overflow-hidden rounded-[4px] border border-zinc-200 bg-white shadow-[0_1px_3px_rgba(16,24,40,0.08)]", className)} data-testid={testid}>
    <header className="flex flex-wrap items-center gap-3 border-b border-zinc-200 bg-zinc-50 px-5 py-3">
      <span className={cn("h-5 w-1 rounded-full", ACCENT[accent])} />
      {Icon && <Icon size={17} className="text-zinc-500" />}
      <h3 className="font-display text-base font-bold tracking-tight text-zinc-900">{title}</h3>
      {right && <div className="ml-auto">{right}</div>}
    </header>
    {children}
  </section>
);

export const SectionTitle = ({ children, sub }) => (
  <div className="mb-3 flex flex-wrap items-center gap-x-3 gap-y-1">
    <h2 className="font-display text-lg font-extrabold tracking-tight text-zinc-900">{children}</h2>
    {sub && <span className="text-xs text-muted-foreground">{sub}</span>}
    <div className="hidden h-px flex-1 bg-zinc-300 sm:block" />
  </div>
);

export const DateRange = ({ start, end, onStart, onEnd, prefix = "filter" }) => (
  <div className="flex items-center gap-2">
    <input type="date" value={start} onChange={(e) => onStart(e.target.value)} data-testid={`${prefix}-start-date`}
      className="h-9 rounded-[4px] border border-input bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" />
    <span className="text-muted-foreground">—</span>
    <input type="date" value={end} onChange={(e) => onEnd(e.target.value)} data-testid={`${prefix}-end-date`}
      className="h-9 rounded-[4px] border border-input bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" />
  </div>
);

export const Segmented = ({ value, onChange, options, prefix }) => (
  <div className="inline-flex rounded-[4px] border border-border bg-white p-0.5">
    {options.map(([v, label]) => (
      <button key={v} onClick={() => onChange(v)} data-testid={`${prefix}-${v || "all"}`}
        className={cn("rounded-[3px] px-3 py-1.5 text-xs font-semibold transition-colors duration-150", value === v ? "bg-zinc-900 text-white" : "text-muted-foreground hover:text-foreground")}>
        {label}
      </button>
    ))}
  </div>
);
